import React, { useEffect, useState, useRef } from "react";
import '../css/ProfessorAdmin.css';

const ProfessorAdmin = () => {
  // State management
  const [professors, setProfessors] = useState([]);
  const [filteredProfessors, setFilteredProfessors] = useState([]);
  const [professorSearchTerm, setProfessorSearchTerm] = useState("");
  const [selectedProf, setSelectedProf] = useState("");
  const [selectedProfName, setSelectedProfName] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("select");
  const [projects, setProjects] = useState([]);
  const [allottedStudents, setAllottedStudents] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [message, setMessage] = useState("");
  const [departments, setDepartments] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [customDuration, setCustomDuration] = useState(false);
  
  // Refs for click outside handling
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [no_of_interns, setNoOfInterns] = useState("");
  const [duration, setDuration] = useState("");
  const [customDurationValue, setCustomDurationValue] = useState("");
  const [mode, setMode] = useState("");
  const [prerequisites, setPrerequisites] = useState("");
  const [dept_id, setDeptId] = useState("");
  const [editingProjectId, setEditingProjectId] = useState(null);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper function to map mode from API to select value
  const mapModeToSelectValue = (apiMode) => {
    if (!apiMode) return "";
    
    const modeStr = String(apiMode).toUpperCase().trim();
    console.log("Mapping mode:", apiMode, "to:", modeStr);
    
    // Handle common variations
    if (modeStr.includes("ONLINE") || modeStr === "ON") return "ONLINE";
    if (modeStr.includes("OFFLINE") || modeStr === "OFF") return "OFFLINE";
    if (modeStr.includes("HYBRID") || modeStr === "HYB") return "HYBRID";
    
    // If no match, return as is
    return modeStr;
  };

  // Filter professors based on search term
  useEffect(() => {
    if (professors.length > 0) {
      const filtered = professors.filter(prof => 
        prof.name.toLowerCase().includes(professorSearchTerm.toLowerCase())
      );
      setFilteredProfessors(filtered);
    }
  }, [professors, professorSearchTerm]);

  // Fetch data functions
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("access_token");

        // Fetch professors
        const profRes = await fetch("/api/admin/professors", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!profRes.ok) throw new Error("Failed to fetch professors");
        const profData = await profRes.json();
        setProfessors(profData);
        setFilteredProfessors(profData);

        // Fetch departments
        setLoadingDepts(true);
        const deptRes = await fetch("/api/admin/departments", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!deptRes.ok) throw new Error("Failed to fetch departments");
        setDepartments(await deptRes.json());

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        setLoadingDepts(false);
      }
    };
    fetchInitialData();
  }, []);

  const fetchProjects = async () => {
    if (!selectedProf) return;
    setLoadingProjects(true);
    try {
      const token = localStorage.getItem("access_token");
      const profId = Number(selectedProf);
      const res = await fetch(`/api/admin/professors/${profId}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setProjects(data);
      } else {
        throw new Error(data.detail || "Failed to fetch projects");
      }
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchAllottedStudents = async () => {
    if (!selectedProf) return;
    setLoadingStudents(true);
    try {
      const token = localStorage.getItem("access_token");
      const profId = Number(selectedProf);
      const res = await fetch(`/api/admin/professors/${profId}/allotted_student`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setAllottedStudents(data);
      } else {
        throw new Error(data.detail || "Failed to fetch students");
      }
    } catch (err) {
      setMessage(err.message);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Handle tab changes
  useEffect(() => {
    if (activeTab === "view") fetchProjects();
    if (activeTab === "allotted") fetchAllottedStudents();
  }, [activeTab, selectedProf]);

  // Handle professor selection
  const handleProfessorSelect = (prof) => {
    setSelectedProf(prof.user.id);
    setSelectedProfName(prof.name);
    setProfessorSearchTerm(prof.name);
    setShowDropdown(false);
  };

  // Handle input change
  const handleInputChange = (e) => {
    setProfessorSearchTerm(e.target.value);
    setShowDropdown(true);
    // Clear selection if user starts typing
    if (selectedProf) {
      setSelectedProf("");
      setSelectedProfName("");
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    // Convert IDs to numbers
    const profId = Number(selectedProf);
    const deptId = Number(dept_id);
    const interns = Number(no_of_interns);

    // Validate IDs
    if (!profId || isNaN(profId)) {
      setMessage("Please select a valid professor.");
      return;
    }
    if (!deptId || isNaN(deptId)) {
      setMessage("Please select a valid department.");
      return;
    }
    if (!title || !description || !interns || !mode) {
      setMessage("Please fill all required fields.");
      return;
    }

    // Handle duration - get value from either select or custom input
    let finalDuration = duration;
    if (customDuration) {
      if (!customDurationValue.trim()) {
        setMessage("Please enter custom duration.");
        return;
      }
      finalDuration = customDurationValue;
    } else if (!duration) {
      setMessage("Please select duration.");
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const url = editingProjectId
        ? `/api/professors/projects/${editingProjectId}`
        : `/api/admin/professors/${profId}/projects`;

      const method = editingProjectId ? "PUT" : "POST";

      const requestBody = {
        title,
        description,
        no_of_interns: interns,
        duration: finalDuration,
        mode,
        dept_id: deptId,
        prerequisites: prerequisites || null,
      };

      console.log("Sending to", url, "with body:", requestBody);

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (!response.ok) {
        let errorMsg = "Failed to save project";
        if (responseData.detail) {
          if (Array.isArray(responseData.detail)) {
            errorMsg = responseData.detail.map(e => e.msg || e).join(", ");
          } else {
            errorMsg = responseData.detail;
          }
        }
        throw new Error(errorMsg);
      }

      setMessage(editingProjectId ? "Project updated!" : "Project added!");
      resetForm();
      fetchProjects();
      setActiveTab("view");
    } catch (err) {
      setMessage(err.message);
    }
  };

  // Fixed handleEdit function - now properly loads the mode
  const handleEdit = (project) => {
    console.log("Editing project - full data:", project);
    console.log("Original mode value:", project.mode, "Type:", typeof project.mode);
    
    setTitle(project.title);
    setDescription(project.description);
    setNoOfInterns(project.no_of_interns.toString());
    
    // Check if duration is from predefined options or custom
    const predefinedDurations = ["1 month", "2 months"];
    if (predefinedDurations.includes(project.duration)) {
      setDuration(project.duration);
      setCustomDuration(false);
      setCustomDurationValue("");
    } else {
      setDuration("");
      setCustomDuration(true);
      setCustomDurationValue(project.duration);
    }
    
    // Fixed: Handle mode properly using the mapping function
    if (project.mode) {
      const mappedMode = mapModeToSelectValue(project.mode);
      console.log("Setting mode to:", mappedMode);
      setMode(mappedMode);
    } else {
      setMode("");
    }
    
    setPrerequisites(project.prerequisites || "");
    setDeptId(project.dept_id.toString());
    setEditingProjectId(project.id);
    setActiveTab("add");
    
    // Final check
    console.log("Form state after edit - mode:", mode);
  };

  // Delete handler
  const handleDelete = async (projectId) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`/api/professors/project/${projectId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMessage("Project deleted");
        fetchProjects();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to delete");
      }
    } catch (err) {
      setMessage(err.message);
    }
  };

  // Helper functions
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setNoOfInterns("");
    setDuration("");
    setCustomDuration(false);
    setCustomDurationValue("");
    setMode("");
    setPrerequisites("");
    setDeptId("");
    setEditingProjectId(null);
  };

  const getSelectedProfessorName = () => {
    return selectedProfName || "Selected Professor";
  };

  const getDepartmentName = (id) => {
    const dept = departments.find(d => Number(d.id) === Number(id));
    return dept ? dept.name : "Unknown Department";
  };

  // Highlight matching text
  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? 
        <mark key={index} className="highlight">{part}</mark> : 
        part
    );
  };

  return (
    <div className="container">
      <h1>Professor Admin</h1>

      {/* Tab navigation */}
      <div className="tab-buttons">
        {["select", "add", "view", "allotted"].map(tab => (
          <button
            key={tab}
            onClick={() => {
              if (!selectedProf && tab !== "select") {
                setMessage("Select a professor first");
                setActiveTab("select");
              } else {
                setActiveTab(tab);
              }
            }}
            className={`tab-button ${activeTab === tab ? "active" : ""}`}
          >
            {tab === "select" ? "Select Professor" :
              tab === "add" ? (editingProjectId ? "Editing" : "Add Project") :
                tab === "view" ? "View Projects" : "Allotted Students"}
          </button>
        ))}
      </div>

      {message && (
        <div className={`message ${message.includes("!") ? "success" : "error"}`}>
          {message}
        </div>
      )}

      {activeTab === "select" && (
        <div className="select-professor">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="professor-search-container" ref={dropdownRef}>
                <label>
                  Search Professor by Name:
                  <div className="search-input-wrapper">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Type to search professor..."
                      value={professorSearchTerm}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      className="search-input"
                      autoComplete="off"
                    />
                    {professorSearchTerm && (
                      <button 
                        className="clear-search"
                        onClick={() => {
                          setProfessorSearchTerm("");
                          setSelectedProf("");
                          setSelectedProfName("");
                          setFilteredProfessors(professors);
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </label>
                
                {showDropdown && filteredProfessors.length > 0 && (
                  <div className="professor-dropdown">
                    {filteredProfessors.map(prof => (
                      <div
                        key={prof.user.id}
                        className={`professor-option ${selectedProf === prof.user.id ? 'selected' : ''}`}
                        onClick={() => handleProfessorSelect(prof)}
                      >
                        <span className="professor-name">
                          {highlightMatch(prof.name, professorSearchTerm)}
                        </span>
                        {prof.department && (
                          <span className="professor-dept">{prof.department}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {showDropdown && filteredProfessors.length === 0 && professorSearchTerm && (
                  <div className="professor-dropdown">
                    <div className="no-results">
                      No professors found matching "{professorSearchTerm}"
                    </div>
                  </div>
                )}
              </div>
              
              {selectedProf && (
                <div className="selected-info">
                  <strong>Selected Professor:</strong> {selectedProfName}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "add" && (
        <div className="project-form">
          <h2>{editingProjectId ? "Edit" : "Add"} Project for {getSelectedProfessorName()}</h2>
          
          {/* Debug display - remove in production */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div style={{ 
              backgroundColor: '#f0f0f0', 
              padding: '10px', 
              marginBottom: '20px',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#333'
            }}>
              <strong>Debug:</strong> Current mode value: "{mode}" (Type: {typeof mode})
            </div>
          )}
           */}
          <form onSubmit={handleSubmit}>
            <label>
              Title:
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </label>

            <label>
              Description:
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
            </label>

            <label>
              Number of Interns:
              <input
                type="number"
                value={no_of_interns}
                onChange={(e) => setNoOfInterns(e.target.value)}
                min="1"
                required
              />
            </label>

            <label>
              Duration:
              <div className="duration-container">
                <select
                  value={customDuration ? "custom" : duration}
                  onChange={(e) => {
                    if (e.target.value === "custom") {
                      setCustomDuration(true);
                      setDuration("");
                    } else {
                      setCustomDuration(false);
                      setDuration(e.target.value);
                      setCustomDurationValue("");
                    }
                  }}
                  required={!customDuration}
                  style={{ marginBottom: customDuration ? '10px' : '0' }}
                >
                  <option value="">Select Duration</option>
                  <option value="1 month">1 month</option>
                  <option value="2 months">2 months</option>
                </select>
              </div>
            </label>

            <label>
              Mode:
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                required
              >
                <option value="">Select Mode</option>
                <option value="ONLINE">ONLINE</option>
                <option value="OFFLINE">OFFLINE</option>
                <option value="HYBRID">HYBRID</option>
              </select>
            </label>

            <label>
              Department:
              <select
                value={dept_id}
                onChange={(e) => setDeptId(e.target.value)}
                required
              >
                <option value="">Select Department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Prerequisites (optional):
              <input
                type="text"
                value={prerequisites}
                onChange={(e) => setPrerequisites(e.target.value)}
                placeholder="Leave empty if none"
              />
            </label>

            <div className="form-actions">
              <button type="submit">
                {editingProjectId ? "Update" : "Create"} Project
              </button>
              {editingProjectId && (
                <button type="button" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {activeTab === "view" && (
        <div className="projects-list">
          <h2>Projects by {getSelectedProfessorName()}</h2>
          {loadingProjects ? (
            <p>Loading projects...</p>
          ) : projects.length === 0 ? (
            <p>No projects found</p>
          ) : (
            <ul>
              {projects.map(project => (
                <li key={project.id}>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <div className="project-details">
                    <span>Department: {getDepartmentName(project.dept_id)}</span>
                    <span>Interns: {project.no_of_interns}</span>
                    <span>Duration: {project.duration}</span>
                    <span>Mode: {project.mode}</span>
                    {project.prerequisites && (
                      <span>Prerequisites: {project.prerequisites}</span>
                    )}
                  </div>
                  <div className="project-actions">
                    <button onClick={() => handleEdit(project)}>Edit</button>
                    <button onClick={() => handleDelete(project.id)}>Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {activeTab === "allotted" && (
        <div className="allotted-students">
          <h2>Students Allotted to {getSelectedProfessorName()}</h2>
          {loadingStudents ? (
            <p>Loading students...</p>
          ) : allottedStudents.length === 0 ? (
            <p>No students allotted</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>SIP ID</th>
                  <th>Name</th>
                  <th>Project</th>
                  <th>Department</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                </tr>
              </thead>
              <tbody>
                {allottedStudents.map(student => (
                  <tr key={student.sip_id}>
                    <td>{student.sip_id}</td>
                    <td>{student.name}</td>
                    <td>{student.project_title}</td>
                    <td>{student.department_name || "N/A"}</td>
                    <td>{student.start_date}</td>
                    <td>{student.end_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfessorAdmin;